import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoreHorizontal, Search } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useDebounce } from "@/hooks/use-debounce";
import { format } from "date-fns";
import { toast } from "sonner";
import { PaginationControls } from "@/components/common/PaginationControl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES } from "@/constants/permissions";
import { MerchantTableSkeleton } from "@/components/merchant/MerchantTableSkeleton";

const RedemptionMerchants = () => {
  const { refreshToken } = useAuth();
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalMerchants, setTotalMerchants] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${
          import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
        }/redemptionMerchant/getRedemptionMerchants`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json, text/plain, */*",
          },
          credentials: "include",
          body: JSON.stringify({
            merchantName: debouncedSearchQuery || null,
            merchantCode: null,
            warehouseCountryId: null,
            pageNumber: currentPage,
            pageSize,
            sortOrder: "desc",
            sortBy: "createdDate",
          }),
        }
      );
      if (response.status === 401 || response.status === 403) {
        const refreshed = await refreshToken();
        if (refreshed) return fetchMerchants();
      }
      if (!response.ok) throw new Error("Failed to fetch merchants");
      const data = await response.json();
      setMerchants(data.redemptionMerchantVOS || []);
      setTotalMerchants(data.totalRecords || 0);
    } catch (err) {
      setError(err?.message || "Failed to fetch merchants");
      toast.error(err?.message || "Failed to fetch merchants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchants();
    // eslint-disable-next-line
  }, [currentPage, pageSize, debouncedSearchQuery]);

  const columns = [
    {
      header: "Merchant Name/Code",
      accessorKey: "merchantName",
      cell: (row) => (
        <div>
          <div>{row.merchantName}</div>
          <div className="text-xs text-gray-500 mt-0.5">{row.merchantCode}</div>
        </div>
      ),
    },
    {
      header: "Merchant Email",
      accessorKey: "merchantEmail",
      cell: (row) => <div className="text-sm">{row.merchantEmail}</div>,
    },
    {
      header: "City/Country",
      accessorKey: "cityCountry",
      cell: (row) => (
        <div>
          <div>{row.city}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {row.countryVO?.name}
          </div>
        </div>
      ),
    },
    {
      header: "Created Date",
      accessorKey: "createdDate",
      cell: (row) => (
        <div>{format(new Date(row.createdDate), "MMM dd, yyyy, h:mm a")}</div>
      ),
    },
    {
      header: "Updated Date",
      accessorKey: "lastUpdatedDate",
      cell: (row) => (
        <div>
          {format(new Date(row.lastUpdatedDate), "MMM dd, yyyy, h:mm a")}
        </div>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white min-w-[8rem] p-1"
          >
            <DropdownMenuItem
              onClick={() => {
                /* TODO: Edit logic */
              }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                navigate(`/rewardpoint/redemption-merchants/view/${row.id}`, {
                  state: row,
                })
              }
            >
              View
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                /* TODO: Delete logic */
              }}
              className="text-red-600"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Redemption Merchants
          </h1>
          <p className="text-gray-500">Manage your redemption merchants</p>
        </div>
        <PermissionGuard
          permissionCode={PERMISSION_CODES.CREATE_REDEMPTION_MERCHANT}
        >
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => navigate("/rewardpoint/redemption-merchants/create")}
          >
            + Add Redemption Merchant
          </Button>
        </PermissionGuard>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1">
          <label
            htmlFor="search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Merchant Name
          </label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              id="search"
              placeholder="Search merchants..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(0);
              }}
            />
          </div>
        </div>
      </div>
      <DataTable
        title="All Redemption Merchants"
        data={loading ? [] : merchants}
        columns={columns}
        customTableBody={loading ? <MerchantTableSkeleton /> : undefined}
      />
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Rows per page:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(0);
            }}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PaginationControls
          totalPages={Math.ceil(totalMerchants / pageSize)}
          currentPage={currentPage}
          onNextHandler={() =>
            setCurrentPage((p) =>
              Math.min(Math.ceil(totalMerchants / pageSize) - 1, p + 1)
            )
          }
          onPrevHandler={() => setCurrentPage((p) => Math.max(0, p - 1))}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
};

export default RedemptionMerchants;
